/**
 * Trạng thái báo giá (DEC-021 A, P07 §5). `won` cũ đã đổi thành `accepted` (migration 0036).
 * `converted` chỉ do P08 (lên đơn) đặt; `expired` do sales bấm (DEC-048, không có job tự chuyển).
 */
export const QUOTE_STATUSES = ['draft', 'sent', 'negotiating', 'accepted', 'converted', 'lost', 'expired', 'cancelled'] as const;
export type QuoteStatus = (typeof QUOTE_STATUSES)[number];

export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  draft: 'Nháp',
  sent: 'Đã gửi khách',
  negotiating: 'Đang đàm phán',
  accepted: 'Đã chốt',
  converted: 'Đã lên đơn',
  lost: 'Không thành công',
  expired: 'Hết hạn',
  cancelled: 'Đã hủy',
};

/** Nguồn tạo báo giá (P07 §4). */
export const QUOTE_SOURCES = ['manual', 'quick_quote', 'boq', 'ai_draft', 'duplicate', 'asia_draft', 'asia_so3'] as const;
export type QuoteSource = (typeof QUOTE_SOURCES)[number];

/** Trạng thái còn được sửa nội dung. Sửa khi đã gửi khách sẽ tăng số revision. */
export const QUOTE_EDITABLE_STATUSES: readonly QuoteStatus[] = ['draft', 'sent', 'negotiating'];

/** Trạng thái đang chờ khách (còn hiệu lực theo ngày). */
export const QUOTE_OPEN_STATUSES: readonly QuoteStatus[] = ['sent', 'negotiating'];

/** Trạng thái đã đóng (có closed_at). */
export const QUOTE_CLOSED_STATUSES: readonly QuoteStatus[] = ['accepted', 'converted', 'lost', 'expired', 'cancelled'];

/** Chuyển trạng thái hợp lệ (P07 §5). Mở lại (→ draft) cần quyền quote.reopen; mở lại từ "Đã chốt" phải ghi lý do. */
export const QUOTE_TRANSITIONS: Record<QuoteStatus, readonly QuoteStatus[]> = {
  draft: ['sent', 'cancelled'],
  sent: ['negotiating', 'accepted', 'lost', 'expired', 'cancelled'],
  negotiating: ['sent', 'accepted', 'lost', 'expired', 'cancelled'],
  accepted: ['converted', 'draft'],
  converted: [],
  lost: ['draft'],
  expired: ['draft'],
  cancelled: ['draft'],
};

export const QUOTE_REOPEN_FROM: readonly QuoteStatus[] = ['accepted', 'lost', 'expired', 'cancelled'];

/** Chỉ P08 (tạo đơn bán) được chuyển sang các trạng thái này, không qua POST /quotes/:id/status. */
export const QUOTE_SYSTEM_ONLY_STATUSES: readonly QuoteStatus[] = ['converted'];

export function canTransition(from: QuoteStatus, to: QuoteStatus): boolean {
  return QUOTE_TRANSITIONS[from].includes(to);
}

/**
 * Kênh gửi báo giá (P07 B2, DEC-111 A). Hệ thống chưa tự gửi Zalo/Email (DEC-037 hoãn): "Gửi khách" = chụp bản +
 * tải PDF đúng bản đó, sales tự gửi và ghi lại kênh. "Qua Sales admin" (DEC-119): Sales admin / Giám đốc gửi thay.
 */
export const QUOTE_SEND_CHANNELS = ['zalo', 'email', 'direct', 'sales_admin', 'other'] as const;
export type QuoteSendChannel = (typeof QUOTE_SEND_CHANNELS)[number];

export const QUOTE_SEND_CHANNEL_LABELS: Record<QuoteSendChannel, string> = {
  zalo: 'Zalo',
  email: 'Email',
  direct: 'Trực tiếp',
  sales_admin: 'Qua Sales admin',
  other: 'Khác',
};

/** Trạng thái được bấm "Gửi khách" / "Gửi lại" (DEC-110 B: gửi lại không đổi trạng thái đã gửi / đang đàm phán). */
export const QUOTE_SENDABLE_STATUSES: readonly QuoteStatus[] = ['draft', 'sent', 'negotiating'];

/**
 * DEC-110 B: sửa sau khi gửi thì lưu đè (revision +1) và CHƯA chụp bản → "Có sửa chưa gửi lại".
 * Báo giá gửi trước P07 B2 không có bản lưu (sentVersionNo = null) → không tính.
 */
export function hasUnsentChanges(q: { status: QuoteStatus; revision: number; sentVersionNo: number | null }): boolean {
  return QUOTE_OPEN_STATUSES.includes(q.status) && q.sentVersionNo !== null && q.revision > q.sentVersionNo;
}

/** P07 B3 (DEC-113): trạng thái được nhắc hết hạn và bấm "Gia hạn" (báo giá đang ở phía khách). */
export const QUOTE_EXTENDABLE_STATUSES: readonly QuoteStatus[] = ['sent', 'negotiating'];

/** DEC-113: lọc "Sắp hết hạn" = còn hiệu lực tới hôm nay … hôm nay + 2 ngày. */
export const QUOTE_EXPIRING_SOON_DAYS = 2;
